import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Ticket, Film, Sparkles, Star, Search, Clapperboard, ChevronRight, ChevronLeft, Tv, LayoutGrid, X, Loader2, History, ThumbsUp, ThumbsDown, LogIn, UserCircle } from 'lucide-react';
import { Movie, RecommendationRequest, ContentType, WatchedItem, User } from './types';
import { MOCK_MOVIES } from './constants';
import MovieCard from './components/MovieCard';
import GoldenRope from './components/GoldenRope';
import AppropriatenessSlider from './components/AppropriatenessSlider';
import HistoryManager, { ArchiveSearchResult } from './components/HistoryManager';
import AuthModal from './components/AuthModal';
import CriticConsultation from './components/CriticConsultation';
import { fetchHistoryFromCloud, saveRatingToCloud, trackTheaterEvent, migrateUserDataIfNeeded } from './firebase';

const App: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [showAuth, setShowAuth] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  
  const [form, setForm] = useState<RecommendationRequest>({
    favorites: '',
    childAges: '',
    vibe: '',
    contentType: 'both',
    ratingSignal: 3,
    history: []
  });
  
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [recommendations, setRecommendations] = useState<Movie[]>(MOCK_MOVIES);
  const [isRevealed, setIsRevealed] = useState(false);
  const [isOpening, setIsOpening] = useState(false);
  const [history, setHistory] = useState<WatchedItem[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);
  
  // Summary Modal States
  const [selectedMovie, setSelectedMovie] = useState<Movie | null>(null);
  const [aiSummary, setAiSummary] = useState<string>('');
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  
  const resultsRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

// USEEFFECT to load and sync user history
  useEffect(() => {
    const syncUserAndHistory = async () => {
      const savedUser = localStorage.getItem('family_critic_user');
      
      if (savedUser) {
        const parsedUser = JSON.parse(savedUser);
        setUser(parsedUser);

        // Reach out to Firebase to get the freshest data
        try {
          // First-run migration: moves legacy username-keyed data to the new
          // UID-keyed structure. Safe to call every time — no-op after success.
          await migrateUserDataIfNeeded(parsedUser.username);
          const cloudHistory = await fetchHistoryFromCloud(parsedUser.username);
          setHistory(cloudHistory);
        } catch (error) {
          console.error("The Critic couldn't sync with the cloud on startup.");
        }
      }
    };

    syncUserAndHistory();
  }, []);

  const handleLogin = async (username: string) => {
    try {
      // First-run migration for this user+device combo.
      await migrateUserDataIfNeeded(username);
      const cloudHistory = await fetchHistoryFromCloud(username);
      setHistory(cloudHistory);
      const newUser = { username, isLoggedIn: true };
      setUser(newUser);
      
      localStorage.setItem('family_critic_user', JSON.stringify(newUser));
      
      setShowAuth(false);
    } catch (error) {
      console.error("The Critic couldn't reach the archives:", error);
      // Optional: show an error message to the user
    }
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('family_critic_user');
  };

  /**
   * Takes the raw movies from AI, searches TMDB, and attaches real poster URLs.
  const fetchRealPosters = async (movies: Movie[]): Promise<Movie[]> => {
  // Process all movies in parallel calling YOUR backend
    const enrichedMovies = await Promise.all(movies.map(async (movie) => {
      try {
        // Call our new secure proxy endpoint
        const response = await fetch(`/api/poster?title=${encodeURIComponent(movie.title)}`);
        
        if (response.ok) {
          const data = await response.json();
          if (data.posterUrl) {
            return { ...movie, posterUrl: data.posterUrl };
          }
        }
        
        return { ...movie, posterUrl: '/movie_notfound.png' };
      } catch (err) {
        console.error(`Failed to fetch poster for ${movie.title}`, err);
        return { ...movie, posterUrl: '/movie_notfound.png' };
      }
    }));
    
    return enrichedMovies;
  };
  */

  const handleRecommend = async (e: React.FormEvent) => {
      e.preventDefault();

      const requestBody = {
          ...form,
          history: history // This now sends the array of rated movies
        };
    
      // Track the request and the vibe requested
      trackTheaterEvent('request_recommendation', {
        vibe: form.vibe,
        content_type: form.contentType
      });

      setLoading(true);
      setIsOpening(false);
      setIsRevealed(false);

      // --- Immediate Scroll ---
      // We scroll right away so the user sees the "Consulting the Critic" 
      // animation and the rope drop in the correct viewport.
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' // Aligns the top of the results area with the top of the screen
        });
      }, 100); // Tiny delay to allow the DOM to prepare for the state change

      // Configure how many total recommendations you want
      const TOTAL_RECOMMENDATIONS = 4; // Change this to 6, 8, 10, etc.
      const FIRST_BATCH_SIZE = 2;      

      try {
        // ====== FIRST BATCH: Get 2 movies quickly ======
        const firstBatchResponse = await fetch('/api/recommendations', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...requestBody, count: FIRST_BATCH_SIZE, totalCount: TOTAL_RECOMMENDATIONS}), // Request only 2
        });

        if (!firstBatchResponse.ok) throw new Error('Backend failed to respond');

        const firstBatch = await firstBatchResponse.json();
        
        if (firstBatch && firstBatch.length > 0) {
          // ✨ SHOW MOVIES IMMEDIATELY with placeholder posters
          const moviesWithPlaceholders = firstBatch.map((movie: Movie) => ({
            ...movie,
            posterUrl: '/movie_notfound.png'
          }));
          setRecommendations(moviesWithPlaceholders);
          
          // Open curtains immediately after first batch
          setLoading(false);
          setTimeout(() => {
            setIsOpening(true);
            setTimeout(() => {
              setIsRevealed(true);
            }, 800);
          }, 600);

          // 🎨 Fetch posters in background and update progressively
          firstBatch.forEach(async (movie: Movie, index: number) => {
            try {
              const response = await fetch(`/api/poster?title=${encodeURIComponent(movie.title)}`);
              if (response.ok) {
                const data = await response.json();
                if (data.posterUrl) {
                  // Update just this one movie's poster as it loads
                  setRecommendations(prev => prev.map((m, i) => 
                    i === index ? { ...m, posterUrl: data.posterUrl } : m
                  ));
                }
              }
            } catch (err) {
              console.error(`Failed to fetch poster for ${movie.title}`, err);
            }
          });
        }

        // ====== SECOND BATCH: Get remaining movies in background ======
        const remainingCount = TOTAL_RECOMMENDATIONS - FIRST_BATCH_SIZE;

        if (remainingCount > 0) {
          setLoadingMore(true);
          const secondBatchResponse = await fetch('/api/recommendations', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...requestBody, count: remainingCount, offset: FIRST_BATCH_SIZE, totalCount: TOTAL_RECOMMENDATIONS}),
          });

          if (secondBatchResponse.ok) {
            const secondBatch = await secondBatchResponse.json();
            
            if (secondBatch && secondBatch.length > 0) {
              // Add second batch with placeholders
              const batch2WithPlaceholders = secondBatch.map((movie: Movie) => ({
                ...movie,
                posterUrl: '/movie_notfound.png'
              }));
              setRecommendations(prev => [...prev, ...batch2WithPlaceholders]);

              // Fetch posters for second batch progressively
              secondBatch.forEach(async (movie: Movie, index: number) => {
                try {
                  const response = await fetch(`/api/poster?title=${encodeURIComponent(movie.title)}`);
                  if (response.ok) {
                    const data = await response.json();
                    if (data.posterUrl) {
                      // Update this movie's poster (offset by 2 for second batch)
                      setRecommendations(prev => prev.map((m, i) => 
                        i === index + FIRST_BATCH_SIZE ? { ...m, posterUrl: data.posterUrl } : m
                      ));
                    }
                  }
                } catch (err) {
                  console.error(`Failed to fetch poster for ${movie.title}`, err);
                }
              });
            }
          }
          setLoadingMore(false);
        }
      } catch (error) {
        console.error("Fetch error:", error);
        setLoading(false);
        setLoadingMore(false);
      }
    };

  const handleMovieClick = async (movie: Movie) => {
      // 1. Find the "enriched" version of the movie from our current state 
      // This ensures we use the one with the correct posterUrl from fetchRealPosters
      const enrichedMovie = recommendations.find(m => m.title === movie.title) || movie;
      
      setSelectedMovie(enrichedMovie); // Use the version with the real URL
      setIsSummaryLoading(true);
      setAiSummary('');

      try {
        const response = await fetch('/api/summary', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ movie: enrichedMovie, context: form }),
        });

        if (!response.ok) throw new Error('Summary fetch failed');

        const data = await response.json();
        setAiSummary(data.summary || "Summary unavailable.");
      } catch (error) {
        setAiSummary("The Critic is temporarily speechless. Please check back shortly.");
      } finally {
        setIsSummaryLoading(false);
      }
    };

  const handleFeedback = async (movie: Movie, liked: boolean) => {
    trackTheaterEvent('movie_feedback', {
        movie_title: movie.title,
        action: liked ? 'liked' : 'disliked'
      });

    if (!history.find(h => h.title === movie.title)) {
      setHistory(prev => [...prev, { title: movie.title, liked, posterUrl: movie.posterUrl }]);
    }
    setRecommendations(prev => prev.filter(m => m.id !== movie.id));

    // 2. Persistent Cloud Save
    if (user?.isLoggedIn) {
      setIsSyncing(true); // Start the star spinning
      try {
        // We pass the username as the 'Document ID'
        await saveRatingToCloud(user.username, movie, liked);
        console.log("The Critic has noted your preference in the cloud.");
      } catch (error) {
        console.error("Cloud save failed, but local state is preserved:", error);
        // Optional: Add a small toast notification here if the save fails
      } finally {
        // Small delay so the user actually sees the star finish its "thought"
        setTimeout(() => setIsSyncing(false), 1000);
      }
    }
  };

  const removeHistoryItem = (title: string) => {
    setHistory(prev => prev.filter(h => h.title !== title));
  };

  /**
   * Handles adding a title to The Archives from the search feature.
   * Mirrors handleFeedback's cloud-sync flow, but since the item isn't in
   * `recommendations`, we skip the recommendation-pruning step.
   * Writes to the same users/{uid}/ratings collection so future recommendations
   * can dedup against it.
   */
  const handleAddFromSearch = async (result: ArchiveSearchResult, liked: boolean) => {
    trackTheaterEvent('archive_add_from_search', {
      title: result.title,
      content_type: result.contentType,
      action: liked ? 'liked' : 'disliked'
    });

    // Guard against double-adds if the UI somehow allows it
    if (history.find(h => h.title.toLowerCase() === result.title.toLowerCase())) {
      return;
    }

    // Optimistic local update — same shape as WatchedItem
    setHistory(prev => [
      ...prev,
      { title: result.title, liked, posterUrl: result.posterUrl || undefined }
    ]);

    if (user?.isLoggedIn) {
      setIsSyncing(true);
      try {
        // Build a payload shaped like what saveRatingToCloud expects.
        // The `id` is already namespaced (tmdb_movie_123 / tmdb_tv_456) so it
        // cannot collide with IDs coming from the AI recommendation flow.
        const ratingPayload = {
          id: result.id,
          title: result.title,
          posterUrl: result.posterUrl || '/movie_notfound.png',
          year: result.year,
          contentType: result.contentType
        };
        await saveRatingToCloud(user.username, ratingPayload, liked, 'archive_search');
        console.log('The Critic has filed your addition to the archives.');
      } catch (error) {
        console.error('Cloud save failed for archive-added title:', error);
      } finally {
        setTimeout(() => setIsSyncing(false), 1000);
      }
    }
  };

  const closeSummary = () => {
    setSelectedMovie(null);
    setAiSummary('');
  };

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const { scrollLeft, clientWidth } = scrollRef.current;
      const scrollTo = direction === 'left' ? scrollLeft - clientWidth : scrollLeft + clientWidth;
      scrollRef.current.scrollTo({ left: scrollTo, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-[#4a0404] selection:bg-[#d4af37] selection:text-black">
      {/* 2. Place this at the very top level of the return for proper layering */}
      
      {/* ... Navigation ... */}
      <nav className="max-w-6xl mx-auto px-4 pt-6 flex justify-between items-center relative z-20" aria-label="Main Navigation">
        <div className="flex gap-4">
          <button 
            onClick={() => setShowHistory(true)}
            className="flex items-center gap-2 text-[#d4af37] text-[10px] font-bold uppercase tracking-widest border border-[#d4af37]/30 px-3 py-1.5 rounded-full hover:bg-[#d4af37]/10 transition-all"
          >
            <History size={14} /> The Archives ({history.length})

            {/* THE SPINNING STAR */}
              <AnimatePresence>
                {isSyncing && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0, rotate: 0 }}
                    animate={{ opacity: 1, scale: 1, rotate: 360 }}
                    exit={{ opacity: 0, scale: 0 }}
                    transition={{ 
                      rotate: { duration: 2, repeat: Infinity, ease: "linear" },
                      default: { duration: 0.3 }
                    }}
                    className="absolute -right-2 -top-2"
                  >
                    <Star size={12} fill="#d4af37" className="text-[#d4af37] drop-shadow-[0_0_8px_rgba(212,175,55,0.8)]" />
                  </motion.div>
                )}
              </AnimatePresence>
          </button>
        </div>
        
        <div>
          {user ? (
            <div className="flex items-center gap-3">
              <span className="text-[10px] text-[#fef3c7]/60 uppercase tracking-widest hidden md:block">{user.username}</span>
              <button 
                onClick={handleLogout}
                className="flex items-center gap-2 text-[#d4af37] text-[10px] font-bold uppercase tracking-widest border border-[#d4af37]/30 px-3 py-1.5 rounded-full hover:bg-[#d4af37]/10 transition-all"
              >
                <UserCircle size={14} /> Exit
              </button>
            </div>
          ) : (
            <button 
              onClick={() => setShowAuth(true)}
              className="flex items-center gap-2 bg-[#d4af37] text-black text-[10px] font-bold uppercase tracking-widest px-4 py-1.5 rounded-full hover:scale-105 transition-all shadow-lg"
            >
              <LogIn size={14} /> Stage Door
            </button>
          )}
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 py-12 md:py-20 relative">
        <header className="text-center mb-16 space-y-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="inline-flex items-center justify-center space-x-2 px-4 py-1 rounded-full border border-[#d4af37]/30 bg-black/30 backdrop-blur-sm"
          >
            <Star size={14} className="text-[#d4af37]" fill="#d4af37" />
            <span className="text-[#d4af37] text-[10px] font-bold tracking-[0.2em] uppercase">Private Selection</span>
            <Star size={14} className="text-[#d4af37]" fill="#d4af37" />
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1 }}
            className="font-marquee text-5xl md:text-7xl font-black text-[#d4af37] drop-shadow-[0_0_20px_rgba(212,175,55,0.4)] tracking-tighter"
          >
            THE FAMILY CRITIC
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-[#fef3c7] text-lg font-serif-elegant italic max-w-2xl mx-auto"
          >
            Curated cinema recommendations for every age, every vibe, and every parental boundary.
          </motion.p>
        </header>

        <main>
          <motion.section 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-black/40 backdrop-blur-md rounded-2xl border border-[#d4af37]/30 p-8 shadow-2xl relative overflow-hidden mb-12"
          >
             {/* ... Form Decoration ... */}
            <div className="absolute top-0 right-0 w-24 h-24 border-t-2 border-r-2 border-[#d4af37]/40 rounded-tr-2xl pointer-events-none"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 border-b-2 border-l-2 border-[#d4af37]/40 rounded-bl-2xl pointer-events-none"></div>

            <form onSubmit={handleRecommend} className="space-y-10 relative z-10">
              {/* ... Rest of your Form inputs (Content Type, Slider, Inputs) ... */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                  <div className="space-y-4">
                      <label className="flex items-center gap-2 text-[#d4af37] text-xs font-bold uppercase tracking-widest">
                          <Clapperboard size={16} /> Content Type
                      </label>
                      <div className="flex bg-[#1a0101]/60 p-1 rounded-lg border border-[#d4af37]/20">
                          {(['movie', 'tv', 'both'] as ContentType[]).map((type) => (
                              <button
                                  key={type}
                                  type="button"
                                  onClick={() => setForm({...form, contentType: type})}
                                  className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-[10px] font-bold uppercase tracking-widest transition-all ${
                                      form.contentType === type 
                                      ? 'bg-[#d4af37] text-black shadow-lg shadow-[#d4af37]/20' 
                                      : 'text-[#d4af37] hover:bg-white/5'
                                  }`}
                              >
                                  {type === 'movie' && <Film size={12} />}
                                  {type === 'tv' && <Tv size={12} />}
                                  {type === 'both' && <LayoutGrid size={12} />}
                                  {type}
                              </button>
                          ))}
                      </div>
                  </div>

                  <AppropriatenessSlider 
                      value={form.ratingSignal} 
                      onChange={(val) => setForm({...form, ratingSignal: val})} 
                  />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="space-y-3">
                  <label htmlFor="favorites" className="flex items-center gap-2 text-[#d4af37] text-xs font-bold uppercase tracking-widest">
                    <Film size={16} /> Family Favorites
                  </label>
                  <input
                    id="favorites"
                    type="text"
                    value={form.favorites}
                    onChange={e => setForm({...form, favorites: e.target.value})}
                    placeholder="Bluey, Shrek, Spider-Verse..."
                    className="w-full bg-black/40 border border-[#d4af37]/30 rounded-lg p-3 text-sm focus:outline-none focus:border-[#d4af37] transition-all placeholder:text-white/20 text-white"
                    required
                  />
                </div>

                <div className="space-y-3">
                  <label htmlFor="childAges" className="flex items-center gap-2 text-[#d4af37] text-xs font-bold uppercase tracking-widest">
                    <Sparkles size={16} /> Child Ages
                  </label>
                  <input
                    id="childAges"
                    type="text"
                    value={form.childAges}
                    onChange={e => setForm({...form, childAges: e.target.value})}
                    placeholder="4, 7, and 12 years old"
                    className="w-full bg-black/40 border border-[#d4af37]/30 rounded-lg p-3 text-sm focus:outline-none focus:border-[#d4af37] transition-all placeholder:text-white/20 text-white"
                    required
                  />
                </div>

                <div className="space-y-3">
                  <label htmlFor="vibe" className="flex items-center gap-2 text-[#d4af37] text-xs font-bold uppercase tracking-widest">
                    <Search size={16} /> The Current Vibe
                  </label>
                  <input
                    id="vibe"
                    type="text"
                    value={form.vibe}
                    onChange={e => setForm({...form, vibe: e.target.value})}
                    placeholder="Calm bedtime, adventure, spooky..."
                    className="w-full bg-black/40 border border-[#d4af37]/30 rounded-lg p-3 text-sm focus:outline-none focus:border-[#d4af37] transition-all placeholder:text-white/20 text-white"
                    required
                  />
                </div>
              </div>

              <div className="flex flex-col items-center gap-4 pt-2">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  disabled={loading}
                  type="submit"
                  className="group relative px-10 py-4 bg-[#d4af37] text-black font-marquee font-black tracking-[0.2em] uppercase text-xl rounded-sm shadow-[0_0_30px_rgba(212,175,55,0.4)] disabled:opacity-50 transition-all overflow-hidden"
                >
                  <div className="absolute top-1/2 -left-3 w-6 h-6 bg-[#4a0404] rounded-full -translate-y-1/2"></div>
                  <div className="absolute top-1/2 -right-3 w-6 h-6 bg-[#4a0404] rounded-full -translate-y-1/2"></div>
                  
                  <span className="relative flex items-center gap-3">
                    <Ticket className="group-hover:rotate-12 transition-transform" />
                    {loading ? 'Consulting the Critic...' : 'Admit All'}
                  </span>
                </motion.button>
              </div>
            </form>
          </motion.section>

          {/* ... Recommendations Display Area ... */}
          <div className="relative mt-20 mb-32 overflow-hidden min-h-[600px] rounded-3xl border border-[#d4af37]/20 bg-black/20">
            <AnimatePresence>
              {loading && <CriticConsultation key="critic-modal" />}
            </AnimatePresence>
            
            <div className="absolute inset-0 z-40 pointer-events-none flex overflow-hidden rounded-3xl">
              <motion.div 
                  animate={{ x: isRevealed ? '-100%' : '0%' }}
                  transition={{ duration: 2, ease: "easeInOut" }}
                  className="w-1/2 h-full bg-[#3a0303] shadow-[10px_0_30px_rgba(0,0,0,0.8)] border-r border-[#d4af37]/20 relative"
              >
                  <div className="absolute inset-0 opacity-40 mix-blend-soft-light bg-[url('https://www.transparenttextures.com/patterns/black-linen.png')]"></div>
              </motion.div>
              
              <motion.div 
                  animate={{ x: isRevealed ? '100%' : '0%' }}
                  transition={{ duration: 2, ease: "easeInOut" }}
                  className="w-1/2 h-full bg-[#3a0303] shadow-[-10px_0_30px_rgba(0,0,0,0.8)] border-l border-[#d4af37]/20 relative"
              >
                  <div className="absolute inset-0 opacity-40 mix-blend-soft-light bg-[url('https://www.transparenttextures.com/patterns/black-linen.png')]"></div>
              </motion.div>
            </div>

            <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 z-50 pointer-events-none px-4">
              <GoldenRope isDropped={isOpening} />
            </div>

            <div className={`relative z-10 w-full h-full pt-20 transition-all duration-1000 ${isRevealed ? 'opacity-100' : 'opacity-0 scale-95'}`} ref={resultsRef}>
              <div className="flex items-end justify-between mb-8 px-12">
                  <div>
                  <div className="flex items-center gap-2 text-[#d4af37] text-sm uppercase font-bold tracking-widest mb-2">
                      <Clapperboard size={18} /> Now Showing
                  </div>
                  <h2 className="font-marquee text-3xl md:text-5xl text-[#d4af37] drop-shadow-[0_0_10px_rgba(212,175,55,0.3)]">The Critic's Picks</h2>
                  </div>
                  
                  <div className="flex gap-4">
                  <button onClick={() => scroll('left')} className="p-3 border border-[#d4af37]/30 rounded-full hover:bg-[#d4af37] hover:text-black transition-all">
                      <ChevronLeft />
                  </button>
                  <button onClick={() => scroll('right')} className="p-3 border border-[#d4af37]/30 rounded-full hover:bg-[#d4af37] hover:text-black transition-all">
                      <ChevronRight />
                  </button>
                  </div>
              </div>

              <div 
                  ref={scrollRef}
                  className="film-strip flex gap-10 overflow-x-auto pb-24 px-12 snap-x"
              >
                  {recommendations.length > 0 ? recommendations.map((movie, idx) => (
                  <div key={movie.id} className="snap-center">
                      <MovieCard 
                        movie={movie} 
                        index={idx} 
                        onClick={handleMovieClick} 
                        onFeedback={handleFeedback}
                      />
                  </div>
                  )) : (
                    <div className="w-full flex flex-col items-center justify-center py-20 text-center space-y-4">
                      <History size={48} className="text-[#d4af37]/20" />
                      <p className="font-marquee text-[#d4af37]/40 uppercase tracking-widest">Stage is empty.</p>
                    </div>
                  )}

                  {/* Loading indicator for second batch */}
                  {loadingMore && (
                    <div className="snap-center flex items-center justify-center min-w-[300px]">
                      <div className="flex flex-col items-center gap-4">
                        <Loader2 size={48} className="text-[#d4af37] animate-spin" />
                        <p className="font-marquee text-[#d4af37] text-xs uppercase tracking-widest">Finding More...</p>
                      </div>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </main>

        {/* ... Rest of Modals (AI Summary, History, Auth) ... */}
        {/* AI Summary Modal */}
        <AnimatePresence>
          {selectedMovie && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/90 backdrop-blur-xl"
            >
              <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                className="bg-[#3a0303] max-w-3xl w-full max-h-[90vh] overflow-y-auto relative rounded-xl border-4 border-[#d4af37] shadow-[0_0_50px_rgba(212,175,55,0.3)] p-8 md:p-12"
              >
                <button onClick={closeSummary} className="absolute top-4 right-4 text-[#d4af37] hover:text-white transition-colors">
                  <X size={32} />
                </button>

                <div className="flex flex-col md:flex-row gap-8">
                  <div className="w-full md:w-1/3 flex-shrink-0">
                    <img 
                      src={selectedMovie.posterUrl} 
                      alt={selectedMovie.title} 
                      className="w-full rounded-lg shadow-2xl border border-[#d4af37]/30"
                    />
                    <div className="mt-6 text-center">
                       <span className="font-marquee text-[#d4af37] text-2xl block">{selectedMovie.rating}</span>
                       <span className="text-white/40 text-[10px] uppercase tracking-widest">Rating</span>
                    </div>
                  </div>

                  <div className="flex-1 space-y-6">
                    <div>
                      <h2 className="font-marquee text-4xl md:text-5xl text-[#d4af37] mb-2">{selectedMovie.title}</h2>
                      <p className="text-[#d4af37]/60 font-serif-elegant italic">{selectedMovie.year} • {selectedMovie.genre.join(', ')}</p>
                    </div>

                    <div className="h-px bg-gradient-to-r from-[#d4af37]/50 via-[#d4af37]/10 to-transparent"></div>

                    <div className="relative min-h-[200px]">
                      {isSummaryLoading ? (
                        <div className="absolute inset-0 flex flex-col items-center justify-center space-y-4">
                           <Loader2 size={48} className="text-[#d4af37] animate-spin" />
                           <p className="font-marquee text-[#d4af37] text-sm uppercase tracking-widest">Drafting Review...</p>
                        </div>
                      ) : (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="prose prose-invert max-w-none">
                          <div className="font-serif-elegant text-[#fef3c7] leading-relaxed text-lg whitespace-pre-wrap">{aiSummary}</div>
                        </motion.div>
                      )}
                    </div>
                    
                    {!isSummaryLoading && (
                      <div className="pt-8 flex flex-col items-center gap-6">
                         <div className="flex gap-4 w-full">
                            <button 
                              onClick={() => { handleFeedback(selectedMovie, true); closeSummary(); }}
                              className="flex-1 flex items-center justify-center gap-2 bg-[#d4af37] text-black font-bold uppercase py-3 rounded-sm hover:scale-105 transition-transform"
                            >
                              <ThumbsUp size={18} /> Loved It
                            </button>
                            <button 
                              onClick={() => { handleFeedback(selectedMovie, false); closeSummary(); }}
                              className="flex-1 flex items-center justify-center gap-2 border border-[#d4af37]/40 text-[#d4af37] font-bold uppercase py-3 rounded-sm hover:bg-[#d4af37] hover:text-black transition-all"
                            >
                              <ThumbsDown size={18} /> Not For Us
                            </button>
                         </div>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Global Modals */}
        <AnimatePresence>
          {showHistory && (
            <HistoryManager 
              history={history} 
              onRemove={removeHistoryItem} 
              onClose={() => setShowHistory(false)} 
              onAddFromSearch={handleAddFromSearch}
            />
          )}
          {showAuth && (
            <AuthModal 
              onLogin={handleLogin} 
              onClose={() => setShowAuth(false)} 
            />
          )}
        </AnimatePresence>

        <footer className="mt-20 py-12 border-t border-[#d4af37]/20 text-center text-[#fef3c7]/40 text-sm font-serif-elegant">
          <p>© {new Date().getFullYear()} The Family Critic. All Rights Reserved.</p>
        </footer>
      </div>
    </div>
  );
};

export default App;
